import { useState, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';

import { useReactToPrint } from "react-to-print"; 

import Menu from "../components/Menu";
import api from "../services/api";

export default function RelatorioDefeitos() {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef(null);

    const component = useRef(null);
    const [printing, setPrinting] = useState(false);

    const [dadosBrutos, setDadosBrutos] = useState(location.state?.dadosRelatorio || []);
    
    const [minMetragem, setMinMetragem] = useState(0);

    const [initialDate, setInitialDate] = useState(location.state?.initialDate ? new Date(location.state.initialDate) : new Date());
    const [finalDate, setFinalDate] = useState(location.state?.finalDate ? new Date(location.state.finalDate) : new Date());

    const buscarDados = () => {
        if (!initialDate || !finalDate) {
            toast.current.show({
                summary: 'Aviso',
                severity: 'warn',
                detail: 'Preencha ambas as datas para prosseguir'
            });
            return;
        }

        let formated1 = initialDate.getFullYear() + '-' + (initialDate.getMonth() + 1) + '-' + initialDate.getDate();
        let formated2 = finalDate.getFullYear() + '-' + (finalDate.getMonth() + 1) + '-' + finalDate.getDate();

        api.get(`defeitos/historico/${formated1}/${formated2}`).then((response) => {
            const dadosHistorico = response.data.historico || [];
            setDadosBrutos(Array.isArray(dadosHistorico) ? dadosHistorico : []);
            
            if (!Array.isArray(dadosHistorico) || dadosHistorico.length === 0) {
                toast.current.show({
                    summary: 'Não encontrado',
                    severity: 'info',
                    detail: 'Nenhum registro encontrado para a data informada.'
                });
            } else {
                toast.current.show({
                    summary: 'Atualizado',
                    severity: 'success',
                    detail: 'Dados do relatório atualizados com sucesso.'
                });
            }
        }).catch((error) => {
            toast.current.show({
                summary: 'Ocorreu um Erro',
                severity: 'error',
                detail: error.response ? error.response.data.message : error.message
            });
        });
    };

    const { relatorioPorDefeito, turmas, totalGeral } = useMemo(() => {
        if (!Array.isArray(dadosBrutos) || dadosBrutos.length === 0) {
            return { relatorioPorDefeito: [], turmas: [], totalGeral: { total: 0 } };
        }

        const turmasUnicas = new Set();
        const defeitoMap = {};
        const totaisDoRelatorio = { total: 0 };
        
        const limiteMetragem = minMetragem === null ? 0 : minMetragem;

        dadosBrutos.forEach(item => {
            const valMetragem = parseFloat(String(item.metragem).replace(',', '.')) || 0;
            
            if (valMetragem >= limiteMetragem) {
                const turma = String(item.turma || "N/D");
                const tear = String(item.tear || "N/D");
                const def = String(item.defeito || "SEM DEFEITO");
                const art = String(item.artigo || "N/D");

                turmasUnicas.add(turma);

                if (!defeitoMap[def]) {
                    defeitoMap[def] = { defeito: def, artigosMap: {}, totais: { total: 0 } };
                }

                if (!defeitoMap[def].artigosMap[art]) {
                    defeitoMap[def].artigosMap[art] = { artigo: art, tearesMap: {}, totais: { total: 0 } };
                }

                if (!defeitoMap[def].artigosMap[art].tearesMap[tear]) {
                    defeitoMap[def].artigosMap[art].tearesMap[tear] = { tear: tear, total: 0 };
                }

                defeitoMap[def].artigosMap[art].tearesMap[tear][turma] = (defeitoMap[def].artigosMap[art].tearesMap[tear][turma] || 0) + valMetragem;
                defeitoMap[def].artigosMap[art].tearesMap[tear].total += valMetragem;

                defeitoMap[def].artigosMap[art].totais[turma] = (defeitoMap[def].artigosMap[art].totais[turma] || 0) + valMetragem;
                defeitoMap[def].artigosMap[art].totais.total += valMetragem;

                defeitoMap[def].totais[turma] = (defeitoMap[def].totais[turma] || 0) + valMetragem;
                defeitoMap[def].totais.total += valMetragem;

                totaisDoRelatorio[turma] = (totaisDoRelatorio[turma] || 0) + valMetragem;
                totaisDoRelatorio.total += valMetragem;
            }
        });

        const turmasArr = Array.from(turmasUnicas).sort();
        
        const blocosDefeito = Object.values(defeitoMap)
            .sort((a, b) => a.defeito.localeCompare(b.defeito))
            .map(bloco => {
                const linhasCustomizadas = [];
                
                const artigos = Object.values(bloco.artigosMap)
                    .sort((a, b) => a.artigo.localeCompare(b.artigo));
                    
                artigos.forEach(artObj => {
                    const teares = Object.values(artObj.tearesMap)
                        .sort((a, b) => a.tear.localeCompare(b.tear, undefined, {numeric: true}));
                        
                    teares.forEach((tearObj, i) => {
                        linhasCustomizadas.push({
                            id: `${bloco.defeito}-${artObj.artigo}-${tearObj.tear}`,
                            isTotal: false,
                            artigoDisplay: i === 0 ? artObj.artigo : '', 
                            tear: tearObj.tear,
                            ...tearObj
                        });
                    });
                    
                    linhasCustomizadas.push({
                        id: `${bloco.defeito}-${artObj.artigo}-total`,
                        isTotal: true,
                        artigoDisplay: `${artObj.artigo} Total`,
                        tear: '',
                        ...artObj.totais
                    });
                });

                return {
                    defeito: bloco.defeito,
                    totais: bloco.totais,
                    linhas: linhasCustomizadas
                };
            });

        return { relatorioPorDefeito: blocosDefeito, turmas: turmasArr, totalGeral: totaisDoRelatorio };
    }, [dadosBrutos, minMetragem]);

    const formatValue = (val) => {
        if (!val || val === 0) return '-';
        return val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const valueBodyTemplate = (rowData, field) => {
        return <span>{formatValue(rowData[field])}</span>;
    };

    const rowClassName = (rowData) => {
        return {
            'linha-subtotal': rowData.isTotal
        };
    };

    const handlePrint = useReactToPrint({
        content: () => component.current,
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                setPrinting(true);
                setTimeout(() => {
                    resolve();
                }, 500);
            });
        },
        onAfterPrint: () => {
            setPrinting(false);
        }
    });

    return (
        <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', paddingBottom: '3rem' }}>
            <Menu />
            <Toast ref={toast} />

            <style type="text/css" media="all">
                {`
                    @media print {
                        @page { 
                            size: landscape; 
                            margin: 10mm; 
                        }
                        body { 
                            -webkit-print-color-adjust: exact; 
                            print-color-adjust: exact; 
                        }
                        .p-datatable .p-datatable-wrapper { 
                            overflow: visible !important; 
                        }
                        .p-datatable .p-datatable-thead > tr > th,
                        .p-datatable .p-datatable-tbody > tr > td,
                        .p-datatable .p-datatable-tfoot > tr > td {
                            padding: 0.4rem !important;
                            font-size: 0.85rem !important;
                        }
                    }
                    .linha-subtotal td {
                        background-color: #e9ecef !important;
                        font-weight: bold !important;
                    }
                `}
            </style>

            <div className="p-m-3 p-mt-4">
                <Card className="p-shadow-2" style={{ borderRadius: '8px' }}>
                    
                    <div className="p-d-flex p-flex-column p-flex-md-row p-jc-between p-ai-start p-ai-md-center p-mb-4" style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '1rem' }}>
                        <div>
                            <h1 className="p-m-0" style={{ color: '#115a77', fontSize: '1.8rem', fontWeight: 'bold' }}>Relatório por Defeito</h1>
                        </div>
                        <Button 
                            className="p-button-text p-button-secondary p-mt-3 p-mt-md-0" 
                            icon="pi pi-arrow-left" 
                            label="Voltar ao Histórico" 
                            onClick={() => navigate('/defeitos/historico')} 
                        />
                    </div>
                    
                    <div className="p-fluid p-formgrid p-grid p-ai-end p-mb-5" style={{ backgroundColor: '#ffffff', padding: '1rem', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                        <div className="p-field p-col-12 p-md-3">
                            <label style={{ fontWeight: '600', color: '#495057' }}>Data Início</label>
                            <Calendar dateFormat="dd/mm/yy" mask="99/99/9999" onChange={e => setInitialDate(e.value)} value={initialDate} showIcon />
                        </div>
                        <div className="p-field p-col-12 p-md-3">
                            <label style={{ fontWeight: '600', color: '#495057' }}>Data Fim</label>
                            <Calendar dateFormat="dd/mm/yy" mask="99/99/9999" onChange={e => setFinalDate(e.value)} value={finalDate} showIcon />
                        </div>
                        <div className="p-field p-col-12 p-md-2">
                            <Button className="p-button-primary" style={{ backgroundColor: '#115a77', borderColor: '#115a77' }} icon="pi pi-search" onClick={buscarDados} />
                        </div>
                        <div className="p-field p-col-12 p-md-2 p-mt-3 p-mt-md-0">
                            <label style={{ fontWeight: '600', color: '#495057' }}>Ocultar abaixo de (m):</label>
                            <InputNumber 
                                value={minMetragem} 
                                onValueChange={(e) => setMinMetragem(e.value)} 
                                mode="decimal" 
                                minFractionDigits={2} 
                                maxFractionDigits={2}
                                showButtons
                                placeholder="0,00"
                                buttonLayout="horizontal"
                                incrementButtonIcon="pi pi-plus"
                                decrementButtonIcon="pi pi-minus"
                                incrementButtonClassName="p-button-secondary p-button-success"
                                decrementButtonClassName="p-button-secondary p-button-danger"
                                style={{ width: '100%' }}
                                inputStyle={{ width: '100%', textAlign: 'center' }}
                            />
                        </div>
                        <div className="p-field p-col-12 p-md-1 p-mt-3 p-mt-md-0 p-d-flex p-flex-column p-jc-end p-ai-end">
                            <label className="p-d-none p-d-md-block">&nbsp;</label> 
                            <Button 
                                className="p-button" 
                                icon="pi pi-print" 
                                onClick={handlePrint} 
                                tooltip="Imprimir Relatório"
                            />
                        </div>
                    </div>

                    {relatorioPorDefeito.length === 0 ? (
                        <div className="p-text-center p-p-5" style={{ color: '#adb5bd' }}>
                            <i className="pi pi-folder-open p-d-block p-mb-3" style={{ fontSize: '3.5rem' }}></i>
                            <h3 style={{ fontWeight: '400' }}>Nenhum dado encontrado para os filtros selecionados.</h3>
                        </div>
                    ) : (
                        <div ref={component} style={{ padding: printing ? '1rem' : '0' }}>
                            {printing && (
                                <div className="p-mb-4 p-text-center">
                                    <h2>Relatório de Defeitos</h2>
                                    <p>Período: {initialDate.toLocaleDateString()} a {finalDate.toLocaleDateString()}</p>
                                    <p>Ocultar abaixo de: {minMetragem}m</p>
                                </div>
                            )}

                            {relatorioPorDefeito.map((blocoDefeito) => {
                                const footerGroup = (
                                    <ColumnGroup>
                                        <Row>
                                            <Column 
                                                footer={`Subtotal do Defeito:`} 
                                                colSpan={2} 
                                                footerStyle={{ textAlign: 'left', backgroundColor: '#e4f7ff', color: '#115a77', fontSize: '1rem', fontWeight: 'bold' }} 
                                            />
                                            {turmas.map(t => (
                                                <Column 
                                                    key={t} 
                                                    footer={formatValue(blocoDefeito.totais[t])} 
                                                    align="right"
                                                    footerStyle={{ backgroundColor: '#e4f7ff', color: '#115a77', fontWeight: 'bold', width: '120px', minWidth: '120px' }} 
                                                />
                                            ))}
                                            <Column 
                                                footer={formatValue(blocoDefeito.totais.total)} 
                                                align="right"
                                                footerStyle={{ backgroundColor: '#e4f7ff', color: '#115a77', fontSize: '1.05rem', fontWeight: 'bold', width: '120px', minWidth: '120px' }} 
                                            />
                                        </Row>
                                    </ColumnGroup>
                                );

                                return (
                                    <div key={blocoDefeito.defeito} className="p-mb-5" style={{ borderRadius: '6px', overflow: 'hidden', border: '1px solid #dee2e6', pageBreakInside: 'avoid' }}>
                                        <div style={{ backgroundColor: '#c6e4f0', borderLeft: '4px solid #115a77', color: '#115a77', padding: '0.8rem 1.5rem', display: 'flex', alignItems: 'center' }}>
                                            <i className="pi pi-minus-circle p-mr-2" style={{ fontSize: '1.1rem' }}></i>
                                            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                {blocoDefeito.defeito}
                                            </span>
                                        </div>

                                        <DataTable
                                            value={blocoDefeito.linhas}
                                            responsiveLayout="scroll"
                                            rowClassName={rowClassName}
                                            footerColumnGroup={footerGroup}
                                            size="small"
                                            style={{ borderTop: '1px solid #dee2e6' }}
                                        >
                                            <Column field="artigoDisplay" header="Artigo" style={{ minWidth: '150px', width: '20%', color: '#495057' }} />
                                            <Column field="tear" header="Tear" style={{ minWidth: '100px', width: '10%', color: '#6c757d' }} />
                                            
                                            {turmas.map(turma => (
                                                <Column 
                                                    key={turma} 
                                                    field={turma} 
                                                    header={`Turma ${turma}`} 
                                                    body={(r) => valueBodyTemplate(r, turma)} 
                                                    align="right"
                                                    style={{ width: '120px', minWidth: '120px' }} 
                                                />
                                            ))}
                                            
                                            <Column 
                                                field="total" 
                                                header="Total Geral" 
                                                body={(r) => valueBodyTemplate(r, 'total')} 
                                                align="right"
                                                style={{ width: '120px', minWidth: '120px', fontWeight: '600' }} 
                                            />
                                        </DataTable>
                                    </div>
                                );
                            })}

                            <div className="p-mt-6" style={{ borderRadius: '8px', border: '1px solid #dee2e6', overflow: 'hidden', pageBreakInside: 'avoid' }}>
                                <div style={{ backgroundColor: '#115a77', color: 'white', padding: '1rem', textAlign: 'center' }}>
                                    <h2 className="p-m-0" style={{ fontSize: '1.4rem', fontWeight: '500' }}>Resumo Geral</h2>
                                </div>
                                <div className="p-d-flex p-flex-wrap p-jc-around p-p-4" style={{ backgroundColor: '#ffffff' }}>
                                    {turmas.map(t => (
                                        <div key={t} className="p-text-center p-p-2">
                                            <div style={{ color: '#6c757d', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase' }}>Turma {t}</div>
                                            <div style={{ color: '#212529', fontSize: '1.4rem', fontWeight: 'bold' }}>{formatValue(totalGeral[t])}m</div>
                                        </div>
                                    ))}
                                    <div className="p-text-center p-p-2">
                                        <div style={{ color: '#115a77', fontSize: '1rem', fontWeight: 'bold', textTransform: 'uppercase' }}>Total</div>
                                        <div style={{ color: '#115a77', fontSize: '2rem', fontWeight: '800' }}>{formatValue(totalGeral.total)}m</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}